<script setup lang="ts">
import { debounce } from "lodash-es";
import { ref, watchEffect, computed } from "vue";
import { useI18n } from "vue-i18n";

import { baseInput, baseSelect, baseTextarea } from "@/common/components";

import { EquipmentType } from "@/common/types/equipment";
import { ItemStatusEnum, EquipmentEnum } from "@/common/types/enum";

const { t, tm } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: Partial<EquipmentType> | null;
    title?: string;
    width?: string;
    isPatch?: boolean;
  }>(),
  {
    width: "100%",
    isPatch: true
  }
);

const DEFAULT_PAYLOAD = {
  code: "",
  description: null,
  status: ItemStatusEnum.AVAILABLE,
  type: EquipmentEnum.EQUIPMENT,
  name: "",
};

const payload = ref<Partial<EquipmentType>>({ ...DEFAULT_PAYLOAD });

const available = computed(
  () => tm("enums.item_status") as Record<string, string>
);

const types = computed(
  () => tm("enums.equipment_type") as Record<string, string>
);

const statusOptions = computed(() => [
  {
    option: available.value[ItemStatusEnum.AVAILABLE],
    value: ItemStatusEnum.AVAILABLE,
  },
  {
    option: available.value[ItemStatusEnum.RESERVED],
    value: ItemStatusEnum.RESERVED,
  },
  {
    option: available.value[ItemStatusEnum.IN_USE],
    value: ItemStatusEnum.IN_USE,
  },
  {
    option: available.value[ItemStatusEnum.UNAVAILABLE],
    value: ItemStatusEnum.UNAVAILABLE,
  },
  {
    option: available.value[ItemStatusEnum.BROKEN],
    value: ItemStatusEnum.BROKEN,
  },
]);

const typeOptions = computed(() => [
  {
    option: types.value[EquipmentEnum.EQUIPMENT],
    value: EquipmentEnum.EQUIPMENT,
  },
  {
    option: types.value[EquipmentEnum.ACCESSORY],
    value: EquipmentEnum.ACCESSORY,
  },
]);

function maskCode(value: string) {
  return value?.replaceAll(" ", "-")?.toUpperCase();
}

function update(key: keyof typeof payload.value) {
  if (props.isPatch)
    emit("update-data", { [key]: payload.value[key] });
  else
    emit("update-data", payload.value);
}

const debounceForm = debounce(update, 1000);

watchEffect(() => {
  if (props.data) {
    payload.value = {
      ...DEFAULT_PAYLOAD,
      ...props.data,
    };
  }
});
</script>

<template>
  <section class="form-content" :style="{ width: width }">
    <form>
      <base-input
        v-model="payload.code"
        :value="payload.code"
        :placeholder="t('common.placeholders.code')"
        :mask="maskCode"
        @input="debounceForm('code')"
      >
        <template #label> {{ t("common.labels.code") }} * </template>
      </base-input>

      <base-input
        v-model="payload.name"
        :value="payload.name"
        :placeholder="t('common.placeholders.name')"
        @input="debounceForm('name')"
      >
        <template #label> {{ t("common.labels.name") }} * </template>
      </base-input>

      <div class="flex flex-row gap-4">
        <base-select
          v-model="payload.type"
          :value="payload.type"
          :options="typeOptions"
          :is-empty="false"
          required
          @change="update('type')"
        >
          <template #label> {{ t("common.labels.type") }} * </template>
        </base-select>

        <base-select
          v-model="payload.status"
          :value="payload.status"
          :options="statusOptions"
          :is-empty="false"
          required
          @change="update('status')"
        >
          <template #label> {{ t("common.labels.status") }} * </template>
        </base-select>
      </div>

      <base-textarea
        v-model="payload.description"
        :value="payload.description"
        :placeholder="t('common.placeholders.describe')"
        @input="debounceForm('description')"
      >
        <template #label>
          {{ t("common.labels.description") }}
        </template>
      </base-textarea>
    </form>
  </section>
</template>

<style lang="scss" scoped>
.form-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.5rem;
    font-weight: bold;
    text-transform: uppercase;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}

@media screen and (max-width: 1024px) {
  .flex-row {
    flex-direction: column;
  }
}
</style>
